import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  tap,
  switchMap,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { AuthActions } from './auth.actions';
import { StorageService } from '../../services/storage.service';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { NgxSpinnerService } from 'ngx-spinner';
import { select, Store } from '@ngrx/store';
import { selectLanguage } from '../language/language.selectors';
import { GENERAL_STATUS } from '../../constants/constants';
import { extractErrorMessage } from '../../utils/extract-error-message.util';
import { HttpErrorResponse } from '@angular/common/http';
const USER_SESSION = 'USER_SESSION';
const USER_SESSION_PRE = 'USER_SESSION_PRE';

export const loadSessionEffect = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    userService = inject(UserService),
  ) =>
    combineLatest([
      actions$.pipe(
        ofType(AuthActions['[Auth]LoadPreviewSession']),
        startWith(AuthActions['[Auth]LoadPreviewSession']()),
      ),
    ]).pipe(
      exhaustMap((action) => {
        const id = JSON.parse(
          storageService.secureStorage.getItem(USER_SESSION),
        );
        if (!id) {
          return of(
            AuthActions['[Auth]LoginFailed']({
              message: '',
            }),
          );
        }
        return userService.getUser(id.data.idusuario).pipe(
          map((response) => {
            return AuthActions['[Auth]LoginSuccess'](response.data);
          }),
          catchError((error) => {
            return of(
              AuthActions['[Auth]LoginFailed']({
                message: '',
              }),
            );
          }),
        );
      }),
    ),
  { functional: true },
);

export const preLoadSessionEffect = createEffect(
  (
    actions$ = inject(Actions),
    storageService = inject(StorageService),
    userService = inject(UserService),
  ) =>
    combineLatest([
      actions$.pipe(
        ofType(AuthActions['[Auth]LoadPrePreviewSession']),
        startWith(AuthActions['[Auth]LoadPrePreviewSession']()),
      ),
      // interval(2000).pipe(startWith(0)),
    ]).pipe(
      exhaustMap((action) => {
        const id = JSON.parse(
          storageService.secureStorage.getItem(USER_SESSION_PRE),
        );
        if (!id) {
          return of(
            AuthActions['[Auth]LoginPre-Failed']({
              message: '',
            }),
          );
        }
        return userService.getUser(id.data.idusuario).pipe(
          map((response) => {
            return AuthActions['[Auth]LoginPre-Success'](response.data);
          }),
          catchError((error) => {
            return of(
              AuthActions['[Auth]LoginPre-Failed']({
                message: '',
              }),
            );
          }),
        );
      }),
    ),
  { functional: true },
);

export const signInEffect = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    toast = inject(ToastService),
    userService = inject(UserService),
    routerService = inject(Router),
    spinner = inject(NgxSpinnerService),
    store = inject(Store),
  ) =>
    actions$.pipe(
      ofType(AuthActions['[Auth]RequestLogin']),
      withLatestFrom(store.pipe(select(selectLanguage))),
      mergeMap(([action, language]) => {
        setTimeout(() => {
          spinner.show();
        }, 100);

        return authService.signIn(action.username, action.password).pipe(
          switchMap((response) =>
            userService.getUser(String(response.data.idusuario)).pipe(
              map((userResponse) => {
                setTimeout(() => {
                  spinner.hide();
                }, 200);

                if (
                  Number(response.data.TFA) === GENERAL_STATUS.ACTIVE
                ) {
                  routerService.navigate(['/auth/two-factory-authentication']);
                  return AuthActions['[Auth]LoginPre-Success'](
                    userResponse.data,
                  );
                }

                toast.default(`Bienvenido, ${userResponse.data.usuario}`);
                routerService.navigate(['']);
                return AuthActions['[Auth]LoginSuccess'](userResponse.data);
              }),
              catchError(() => {
                setTimeout(() => {
                  spinner.hide();
                }, 200);
                return of(
                  AuthActions['[Auth]LoginFailed']({
                    message: 'No se pudo cargar el perfil del usuario.',
                  }),
                );
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            setTimeout(() => {
              spinner.hide();
            }, 200);

            const message = extractErrorMessage(error, language, 'Error al iniciar sesión.');

            return of(
              AuthActions['[Auth]LoginFailed']({
                message,
              }),
            );
          }),
        );
      }),
    ),

  { functional: true },
);

export const signOutEffect = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) =>
    actions$.pipe(
      ofType(AuthActions['[Auth]Logout']),
      tap(() => authService.logOut()),
    ),

  { functional: true, dispatch: false },
);

export const activeTwoFactorAuth = createEffect(
  (
    actions$ = inject(Actions),
    twoFactorAuthService = inject(UserService),
    toast = inject(ToastService),
  ) =>
    actions$.pipe(
      ofType(AuthActions['[Auth]TwoFactorAuth']),
      exhaustMap((action) => {
        return twoFactorAuthService
          .twoFactorAuth(action._id, action.twoFactorAuth)
          .pipe(
            map((response) => {
              toast.default('Autenticación de 2 pasos activada!');
              return AuthActions['[Auth]UpdateUser'](response.data);
            }),
            catchError((error) => {
              toast.error('Código Incorrecto');
              return of(
                AuthActions['[Auth]LoginFailed']({
                  message: '',
                }),
              );
            }),
          );
      }),
    ),
  { functional: true },
);

export const desactivetwoFactorAuth = createEffect(
  (
    actions$ = inject(Actions),
    twoDesactiveFactorAuthService = inject(UserService),
    toast = inject(ToastService),
  ) =>
    actions$.pipe(
      ofType(AuthActions['[Auth]DesactiveTwoFactorAuth']),
      exhaustMap((action) => {
        return twoDesactiveFactorAuthService
          .twoDesactiveFactorAuth(action._id)
          .pipe(
            map((response) => {
              toast.default('Autenticación de 2 pasos desactivada!');
              return AuthActions['[Auth]UpdateUser'](response.data);
            }),
            catchError((error) => {
              toast.error('Error al configurar');
              return of(
                AuthActions['[Auth]LoginFailed']({
                  message: '',
                }),
              );
            }),
          );
      }),
    ),
  { functional: true },
);

export const updateUser = createEffect(
  (
    actions$ = inject(Actions),
    userService = inject(UserService),
    toastr = inject(ToastrService),
  ) =>
    actions$.pipe(
      ofType(AuthActions['[Auth]RequestUpdateUser']),
      exhaustMap((action) =>
        userService.updateProfile(action.updatedUser).pipe(
          map((response) => {
            toastr.success(`Actualizado Con Éxito`);
            return AuthActions['[Auth]UpdateUser'](response.data);
          }),
          catchError((error: HttpErrorResponse) => {
            toastr.error('Error Al Actualizar');
            return of(
              AuthActions['[Auth]UpdateFailed']({
                message: extractErrorMessage(error, 'es', 'Error al actualizar.'),
              }),
            );
          }),
        ),
      ),
    ),

  { functional: true },
);
